import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2, ArrowRight, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const { t } = useTranslation();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [message, setMessage] = useState(t('payment.checkingMessage'));

  const checkPaymentStatus = useCallback(async () => {
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setStatus('error');
        setMessage(t('payment.errorMessage'));
        return;
      }

      try {
        const response = await axios.get(
          `${API}/payments/checkout/status/${sessionId}`,
          { withCredentials: true }
        );

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setMessage(t('payment.successMessage'));
          await refreshUser();
          return;
        } else if (response.data.status === 'expired') {
          setStatus('error');
          setMessage(t('payment.errorExpired'));
          return;
        }

        // Continue polling
        attempts++;
        setTimeout(poll, pollInterval);
      } catch (error) {
        setStatus('error');
        setMessage(t('payment.errorGeneric'));
      }
    };

    poll();
  }, [sessionId, refreshUser, t]);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId, checkPaymentStatus]);

  return (
    <div className="hussar-payment-page">
      <div className="hussar-payment-container">
        <Card className="hussar-payment-card">
          {status === 'checking' && (
            <>
              <Loader2 className="hussar-payment-icon animate-spin" style={{ color: '#00D4FF' }} />
              <h1 className="hussar-payment-title">{t('payment.checking')}</h1>
              <p className="hussar-payment-message">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="hussar-payment-icon" style={{ color: '#00D4FF' }} />
              <h1 className="hussar-payment-title">{t('payment.successTitle')}</h1>
              <p className="hussar-payment-message">
                {t('payment.successMessage')}
              </p>
              <div
                className="hussar-payment-honor"
                data-testid="payment-success-honor-pledge"
                onClick={() => navigate('/')}
                role="button"
              >
                <ShieldCheck className="hussar-payment-honor-icon" />
                <div className="hussar-payment-honor-text">
                  <strong>{t('payment.honorPledgeTitle')}</strong>
                  <span>{t('payment.honorPledgeText')}</span>
                </div>
              </div>
              <div className="hussar-payment-actions">
                <Button
                  onClick={() => navigate('/app')}
                  className="hussar-btn-primary hussar-btn-large"
                  data-testid="payment-success-start-btn"
                >
                  {t('payment.startUsing')}
                  <ArrowRight className="w-5 h-5" />
                </Button>
                <Button
                  onClick={() => navigate('/subscription')}
                  className="hussar-btn-secondary"
                  data-testid="payment-success-view-sub-btn"
                >
                  {t('payment.viewSubscription')}
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="hussar-payment-icon" style={{ fontSize: '64px' }}>⚠️</div>
              <h1 className="hussar-payment-title">{t('payment.errorTitle')}</h1>
              <p className="hussar-payment-message">{message}</p>
              <Button
                onClick={() => navigate('/pilot-publiczny')}
                className="hussar-btn-secondary"
                data-testid="payment-error-retry-btn"
              >
                {t('payment.tryAgain')}
              </Button>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccess;
